import { useState, useEffect } from "react";
import api from "../../axios";

const EditUserModal = ({ isOpen, onClose, user, refreshUsers }) => {
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [roleId, setRoleId] = useState(user?.role_id || "");
  const [company, setCompany] = useState(user?.company || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [nin, setNin] = useState(user?.nin || "");
  const [roles, setRoles] = useState([]);
  const [errors, setErrors] = useState({}); // Store validation errors
  const [loading, setLoading] = useState(false);
  const [department, setDepartment] = useState(user?.department || "");


  // Fetch available roles when modal opens
  useEffect(() => {
    if (isOpen) {
      api.get("/roles")
        .then((response) => setRoles(response.data))
        .catch((error) => console.error("Error fetching roles:", error));

      // Reset user data when modal opens
      if (user) {
        setName(user.name);
        setEmail(user.email);
        setRoleId(user.role?.id || "");
        setCompany(user.company || "");
        setPhone(user.phone || "");
        setNin(user.nin || "");
        setErrors({}); // Clear previous errors
      }
    }
  }, [isOpen, user]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({}); // Clear previous errors

    try {
      await api.put(`/users/${user.id}`, {
        name,
        email,
        role_id: roleId,
        company,
        phone,
        nin,
        department
      });

      refreshUsers(); // Refresh users list in main page
      onClose(); // Close modal
    } catch (error) {
      if (error.response && error.response.data.errors) {
        setErrors(error.response.data.errors); // Store validation errors
      } else {
        console.error("Error updating user:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  return isOpen ? (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
      <h2 className="text-xl font-semibold mb-4">Edit User</h2>
      <form className="space-y-4" onSubmit={handleSubmit}>
        {/* Row 1: Name and Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name[0]}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email[0]}</p>}
          </div>
        </div>
  
        {/* Row 2: Role and Company */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              required
            >
              <option value="">Select role</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
              <option value="supervisor">Supervisor</option>
              <option value="admin">Admin</option>
            </select>
            {errors.role_id && <p className="text-red-500 text-xs mt-1">{errors.role_id[0]}</p>}
          </div>
  
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
            {errors.company && <p className="text-red-500 text-xs mt-1">{errors.company[0]}</p>}
          </div>
        </div>
  
        {/* Row 3: Department and Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
            <input 
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Enter department"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
            />
            {errors.department && <p className="text-red-500 text-xs mt-1">{errors.department[0]}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone[0]}</p>}
          </div>
        </div>
  
        {/* Row 4: NIN by itself */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">NIN</label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            value={nin}
            onChange={(e) => setNin(e.target.value)}
          />
          {errors.nin && <p className="text-red-500 text-xs mt-1">{errors.nin[0]}</p>}
        </div>
  
        {/* General Error Message */}
        {errors.general && (
          <p className="text-red-500 text-sm">{errors.general}</p>
        )}
  
        {/* Buttons */}
        <div className="flex justify-end space-x-3 pt-3">
          <button
            type="button"
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            disabled={loading}
          >
            {loading ? "Updating..." : "Update User"}
          </button>
        </div>
      </form>
    </div>
  </div>
  ) : null;
};

export default EditUserModal;

